import React, { useState } from 'react';
import { X, Calendar, Clock, Mail, Plus, Trash2 } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ReportSchedulingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduleCreated: () => void;
}

interface ScheduleFormData {
  reportType: string;
  name: string;
  description: string;
  format: string;
  frequency: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string;
  timezone: string;
  recipients: string[];
  filters: {
    startDate: string;
    endDate: string;
    clientIds: string[];
    userIds: string[];
    taskTypes: string[];
    departments: string[];
  };
}

export const ReportSchedulingModal: React.FC<ReportSchedulingModalProps> = ({
  isOpen,
  onClose,
  onScheduleCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [formData, setFormData] = useState<ScheduleFormData>({
    reportType: '',
    name: '',
    description: '',
    format: 'pdf',
    frequency: 'monthly',
    time: '09:00',
    timezone: 'UTC',
    recipients: [],
    filters: {
      startDate: '',
      endDate: '',
      clientIds: [],
      userIds: [],
      taskTypes: [],
      departments: []
    }
  });

  const reportTypes = [
    { value: 'financial', label: 'Financial Report' },
    { value: 'time-tracking', label: 'Time Tracking Report' },
    { value: 'attendance', label: 'Attendance Report' },
    { value: 'client-activity', label: 'Client Activity Report' },
    { value: 'task-performance', label: 'Task Performance Report' },
    { value: 'comprehensive', label: 'Comprehensive Report' }
  ];

  const frequencies = [
    { value: 'daily', label: 'Daily', description: 'Every day at specified time' },
    { value: 'weekly', label: 'Weekly', description: 'Every week on specified day' },
    { value: 'monthly', label: 'Monthly', description: 'Every month on specified date' },
    { value: 'quarterly', label: 'Quarterly', description: 'Every 3 months' },
    { value: 'yearly', label: 'Yearly', description: 'Once per year' }
  ];

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Asia/Kolkata', label: 'India Standard Time' }
  ];

  const handleInputChange = (field: keyof ScheduleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFilterChange = (field: keyof ScheduleFormData['filters'], value: any) => {
    setFormData(prev => ({
      ...prev,
      filters: { ...prev.filters, [field]: value }
    }));
  };

  const addRecipient = () => {
    if (newRecipient && newRecipient.includes('@')) {
      if (!formData.recipients.includes(newRecipient)) {
        handleInputChange('recipients', [...formData.recipients, newRecipient]);
        setNewRecipient('');
      } else {
        toast.error('Email already added');
      }
    } else {
      toast.error('Please enter a valid email address');
    }
  };

  const removeRecipient = (email: string) => {
    handleInputChange('recipients', formData.recipients.filter(r => r !== email));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.reportType) {
      toast.error('Please select a report type');
      return;
    }

    if (formData.recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/firm-admin/reports/schedule', formData);
      
      if (response.data.success) {
        toast.success('Report scheduled successfully');
        onScheduleCreated();
        onClose();
        
        // Reset form
        setFormData({
          reportType: '',
          name: '',
          description: '',
          format: 'pdf',
          frequency: 'monthly',
          time: '09:00',
          timezone: 'UTC',
          recipients: [],
          filters: {
            startDate: '',
            endDate: '',
            clientIds: [],
            userIds: [],
            taskTypes: [],
            departments: []
          }
        });
      }
    } catch (error: any) {
      console.error('Error scheduling report:', error);
      toast.error('Failed to schedule report');
    } finally {
      setLoading(false);
    }
  };

  const generateScheduleName = (type: string, frequency: string): string => {
    const reportType = reportTypes.find(rt => rt.value === type);
    return `${reportType?.label || 'Report'} - ${frequency.charAt(0).toUpperCase() + frequency.slice(1)}`;
  };

  const handleReportTypeChange = (type: string) => {
    handleInputChange('reportType', type);
    if (!formData.name) {
      handleInputChange('name', generateScheduleName(type, formData.frequency));
    }
  };

  const handleFrequencyChange = (frequency: string) => {
    handleInputChange('frequency', frequency);
    if (formData.reportType && !formData.name) {
      handleInputChange('name', generateScheduleName(formData.reportType, frequency));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Schedule Report</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Report Type and Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type *
              </label>
              <select
                value={formData.reportType}
                onChange={(e) => handleReportTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              >
                <option value="">Select report type</option>
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter schedule name"
                required
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional description for this scheduled report"
            />
          </div>

          {/* Schedule Configuration */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Schedule Configuration</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frequency *
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  {frequencies.map(freq => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Day of Week
                  </label>
                  <select
                    value={formData.dayOfWeek || ''}
                    onChange={(e) => handleInputChange('dayOfWeek', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {daysOfWeek.map(day => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Day of Month
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dayOfMonth || ''}
                    onChange={(e) => handleInputChange('dayOfMonth', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="1-31"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={formData.format}
                  onChange={(e) => handleInputChange('format', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="pdf">PDF Document</option>
                  <option value="csv">CSV Spreadsheet</option>
                  <option value="excel">Excel Workbook</option>
                </select>
              </div>
            </div>
          </div>

          {/* Email Recipients */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Email Recipients</h3>
            
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="email"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter email address"
              />
              <button
                type="button"
                onClick={addRecipient}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </button>
            </div>

            {formData.recipients.length > 0 && (
              <div className="space-y-2">
                {formData.recipients.map((email, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">{email}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.reportType || formData.recipients.length === 0}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
